import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { api, User } from './api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  isStoreManager: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token');
    if (savedToken) {
      verifyToken(savedToken);
    } else {
      setLoading(false);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  const scheduleTokenRefresh = (authToken: string) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // 50분 후에 토큰 갱신
    refreshTimerRef.current = setTimeout(() => {
      refreshToken(authToken);
    }, 50 * 60 * 1000);
  };

  const refreshToken = async (currentToken: string) => {
    try {
      const data = await api('/api/v1/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${currentToken}`,
        },
      });

      if (data.success && data.token) {
        localStorage.setItem('auth_token', data.token);
        setToken(data.token);
        setUser(data.user);
        scheduleTokenRefresh(data.token);
      } else {
        await signOut();
        if (window.location.pathname !== '/login') {
          alert('세션이 만료되었습니다. 다시 로그인해주세요.');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      await signOut();
      if (window.location.pathname !== '/login') {
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.href = '/login';
      }
    }
  };

  const verifyToken = async (authToken: string) => {
    try {
      const data = await api('/api/v1/auth/verify', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (data.success) {
        setUser(data.user);
        setToken(authToken);
        scheduleTokenRefresh(authToken);
      } else {
        localStorage.removeItem('auth_token');
        setToken(null);
        setUser(null);

        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/') {
          alert('세션이 만료되었습니다. 다시 로그인해주세요.');
          window.location.href = '/login';
        }
      }
    } catch (error) {
      console.error('Token verification error:', error);
      localStorage.removeItem('auth_token');
      setToken(null);
      setUser(null);

      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/') {
        alert('세션이 만료되었습니다. 다시 로그인해주세요.');
        window.location.href = '/login';
      }
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string): Promise<User> => {
    try {
      setLoading(true);
      const data = await api('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (!data.success) {
        throw new Error(data.error || '로그인에 실패했습니다.');
      }

      // 관리자 또는 매장 관리자만 로그인 가능
      if (data.user.role !== 'super_admin' && data.user.role !== 'store_manager') {
        throw new Error('관리자 권한이 없습니다.');
      }

      localStorage.setItem('auth_token', data.token);
      setToken(data.token);
      setUser(data.user);
      scheduleTokenRefresh(data.token);

      return data.user;
    } catch (error: any) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }
    localStorage.removeItem('auth_token');
    setToken(null);
    setUser(null);
  };

  const isAdmin = user?.role === 'super_admin';
  const isStoreManager = user?.role === 'store_manager';

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut, isAdmin, isStoreManager, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
